import { useState, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { ArrowLeftRight, Plus, Eye, Check, Ban, Truck, PackageCheck, FileText, Trash2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useArticleUnits } from '../hooks/useArticleUnits';
import { fmtDateTime } from '../lib/datetime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function userCan(user, mod, action) {
  if (!user) return false;
  if (user.role === 'admin') return true;
  return user.permissions?.[mod]?.[action] === true;
}

const STATUS_META = {
  submitted: { label: 'Pendiente de aprobación', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  approved: { label: 'Aprobada · lista para despachar', cls: 'bg-blue-100 text-blue-800 border-blue-300' },
  dispatched: { label: 'Despachada · pendiente recepción', cls: 'bg-purple-100 text-purple-800 border-purple-300' },
  received: { label: 'Recibida', cls: 'bg-green-100 text-green-800 border-green-300' },
  rejected: { label: 'Rechazada', cls: 'bg-red-100 text-red-700 border-red-300' },
  cancelled: { label: 'Cancelada', cls: 'bg-slate-100 text-slate-600 border-slate-300' },
};

function AreaTransfers() {
  const { user } = useOutletContext();
  const { unitOf } = useArticleUnits();
  const [transfers, setTransfers] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ requesting_area_id: '', providing_area_id: '', notes: '', items: [] });
  const [provStock, setProvStock] = useState([]);
  const [newItem, setNewItem] = useState({ article_id: '', quantity: '' });
  const [detail, setDetail] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const isAdminLike = user?.role === 'admin' || user?.role === 'supervisor';

  useEffect(() => {
    (async () => {
      await fetchAll();
    })();
  }, []);

  const fetchAll = async () => {
    try {
      const [t, a] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/area-transfers`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/internal-areas`, { withCredentials: true }),
      ]);
      setTransfers(t.data || []);
      setAreas((a.data || []).filter(x => x.is_active));
    } catch (e) {
      if (e?.response?.status === 403) toast.error('No tenés permiso para ver transferencias.');
      else toast.error('Error al cargar transferencias');
    } finally {
      setLoading(false);
    }
  };

  const isMemberOf = (areaId) => myAreaIdSet.has(areaId);

  // Áreas donde el usuario puede PEDIR (es miembro)
  const myAreaIdSet = useMemo(() => {
    const s = new Set();
    for (const a of areas) {
      if (isAdminLike || (a.member_user_ids || []).includes(user?.user_id)) s.add(a.area_id);
    }
    return s;
  }, [areas, isAdminLike, user]);
  const myAreas = areas.filter(a => myAreaIdSet.has(a.area_id));

  const openCreate = () => {
    setForm({ requesting_area_id: myAreas[0]?.area_id || '', providing_area_id: '', notes: '', items: [] });
    setProvStock([]);
    setNewItem({ article_id: '', quantity: '' });
    setCreateOpen(true);
  };

  const loadProvidingStock = async (areaId) => {
    setForm(f => ({ ...f, providing_area_id: areaId, items: [] }));
    setProvStock([]);
    if (!areaId) return;
    try {
      const r = await axios.get(`${BACKEND_URL}/api/internal-areas/${areaId}/stock?for_transfer=1`, { withCredentials: true });
      setProvStock(r.data?.items || []);
    } catch (e) {
      toast.error('No se pudo cargar el stock del área que entrega');
    }
  };

  const addItem = () => {
    if (!newItem.article_id || !newItem.quantity || Number(newItem.quantity) <= 0) {
      return toast.error('Elegí un artículo y una cantidad válida');
    }
    const stockItem = provStock.find(s => s.article_id === newItem.article_id);
    if (!stockItem) return;
    if (Number(newItem.quantity) > stockItem.total_stock) {
      return toast.error(`Solo hay ${stockItem.total_stock}u disponibles`);
    }
    if (form.items.some(i => i.article_id === newItem.article_id)) {
      return toast.error('Ese artículo ya está en la lista');
    }
    setForm(f => ({
      ...f,
      items: [...f.items, { article_id: newItem.article_id, article_name: stockItem.article_name, quantity: Number(newItem.quantity), available: stockItem.total_stock }],
    }));
    setNewItem({ article_id: '', quantity: '' });
  };

  const removeItem = (aid) => setForm(f => ({ ...f, items: f.items.filter(i => i.article_id !== aid) }));

  const submitCreate = async () => {
    if (!form.requesting_area_id || !form.providing_area_id) return toast.error('Elegí el área que pide y la que entrega');
    if (form.requesting_area_id === form.providing_area_id) return toast.error('Las áreas deben ser distintas');
    if (form.items.length === 0) return toast.error('Agregá al menos un artículo');
    try {
      setSubmitting(true);
      await axios.post(`${BACKEND_URL}/api/area-transfers`, {
        requesting_area_id: form.requesting_area_id,
        providing_area_id: form.providing_area_id,
        items: form.items.map(i => ({ article_id: i.article_id, quantity: i.quantity })),
        notes: form.notes || null,
      }, { withCredentials: true });
      toast.success('Solicitud de transferencia creada');
      setCreateOpen(false);
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo crear la solicitud');
    } finally {
      setSubmitting(false);
    }
  };

  const doAction = async (tid, action, body) => {
    try {
      await axios.post(`${BACKEND_URL}/api/area-transfers/${tid}/${action}`, body || {}, { withCredentials: true });
      const msgs = { approve: 'Aprobada', reject: 'Rechazada', dispatch: 'Despachada', cancel: 'Cancelada', 'confirm-receipt': 'Recepción confirmada' };
      toast.success(msgs[action] || 'Listo');
      await fetchAll();
      setDetail(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo completar la acción');
    }
  };

  const openPdf = (tid) => window.open(`${BACKEND_URL}/api/area-transfers/${tid}/pdf`, '_blank', 'noopener');

  const fmtDate = (d) => (d ? fmtDateTime(d) : '—');

  const renderActions = (t) => {
    const canProvide = isMemberOf(t.providing_area_id);
    const canRequest = isMemberOf(t.requesting_area_id);
    return (
      <div className="flex flex-wrap gap-1 justify-end">
        <Button size="sm" variant="ghost" onClick={() => setDetail(t)} data-testid={`atr-view-${t.transfer_id}`}><Eye className="w-4 h-4" /></Button>
        {t.status === 'submitted' && canProvide && (
          <>
            <Button size="sm" variant="outline" className="text-green-700" onClick={() => doAction(t.transfer_id, 'approve')} data-testid={`atr-approve-${t.transfer_id}`}><Check className="w-4 h-4 mr-1" />Aprobar</Button>
            <Button size="sm" variant="outline" className="text-red-700" onClick={() => doAction(t.transfer_id, 'reject')} data-testid={`atr-reject-${t.transfer_id}`}><Ban className="w-4 h-4" /></Button>
          </>
        )}
        {t.status === 'approved' && canProvide && (
          <Button size="sm" variant="outline" className="text-purple-700" onClick={() => doAction(t.transfer_id, 'dispatch')} data-testid={`atr-dispatch-${t.transfer_id}`}><Truck className="w-4 h-4 mr-1" />Despachar</Button>
        )}
        {(t.status === 'submitted' || t.status === 'approved') && canRequest && (
          <Button size="sm" variant="ghost" className="text-slate-500" onClick={() => doAction(t.transfer_id, 'cancel')} data-testid={`atr-cancel-${t.transfer_id}`}><Trash2 className="w-4 h-4" /></Button>
        )}
        {t.status === 'dispatched' && canRequest && userCan(user, 'pedidos_internos', 'recibir') && (
          <Button size="sm" variant="outline" className="text-teal-700" onClick={() => doAction(t.transfer_id, 'confirm-receipt')} data-testid={`atr-receive-${t.transfer_id}`}><PackageCheck className="w-4 h-4 mr-1" />Recibir</Button>
        )}
        {(t.status === 'dispatched' || t.status === 'received') && (
          <Button size="sm" variant="ghost" onClick={() => openPdf(t.transfer_id)} data-testid={`atr-pdf-${t.transfer_id}`}><FileText className="w-4 h-4" /></Button>
        )}
      </div>
    );
  };

  if (loading) return <div className="p-8 text-center text-muted-foreground">Cargando…</div>;

  return (
    <div className="space-y-6" data-testid="area-transfers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><ArrowLeftRight className="w-6 h-6 text-blue-600" /> Transferencias entre Áreas</h1>
          <p className="text-sm text-muted-foreground">Una área solicita medicación a otra. El área que entrega aprueba, despacha y firma el acuse; el stock se mueve entre subdepósitos.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchAll}><RefreshCw className="w-4 h-4 mr-2" />Refrescar</Button>
          {userCan(user, 'pedidos_internos', 'crear') && myAreas.length > 0 && (
            <Button onClick={openCreate} data-testid="new-transfer-button"><Plus className="w-4 h-4 mr-2" />Nueva solicitud</Button>
          )}
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Solicitudes</CardTitle></CardHeader>
        <CardContent>
          {transfers.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground">No hay transferencias todavía.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nº</TableHead>
                  <TableHead>Pide</TableHead>
                  <TableHead>Entrega</TableHead>
                  <TableHead>Ítems</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transfers.map(t => (
                  <TableRow key={t.transfer_id} data-testid={`atr-row-${t.transfer_id}`}>
                    <TableCell className="font-mono text-xs">{t.transfer_number}</TableCell>
                    <TableCell className="text-sm font-medium">{t.requesting_area_name}</TableCell>
                    <TableCell className="text-sm">{t.providing_area_name}</TableCell>
                    <TableCell className="text-sm">{(t.items || []).length}</TableCell>
                    <TableCell><Badge variant="outline" className={STATUS_META[t.status]?.cls}>{STATUS_META[t.status]?.label || t.status}</Badge></TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{fmtDate(t.created_at)}</TableCell>
                    <TableCell>{renderActions(t)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Crear solicitud */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="transfer-create-dialog">
          <DialogHeader>
            <DialogTitle>Nueva solicitud de transferencia</DialogTitle>
            <DialogDescription>Tu área pide medicación a otra área. Elegí de qué área querés recibir y qué medicación necesitás.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Área que pide (mi área) *</Label>
                <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={form.requesting_area_id}
                  onChange={e => setForm({ ...form, requesting_area_id: e.target.value })} data-testid="transfer-requesting-area">
                  {myAreas.map(a => <option key={a.area_id} value={a.area_id}>{a.name}</option>)}
                </select>
              </div>
              <div>
                <Label>Área que entrega *</Label>
                <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={form.providing_area_id}
                  onChange={e => loadProvidingStock(e.target.value)} data-testid="transfer-providing-area">
                  <option value="">Seleccionar…</option>
                  {areas.filter(a => a.area_id !== form.requesting_area_id).map(a => <option key={a.area_id} value={a.area_id}>{a.name}</option>)}
                </select>
              </div>
            </div>

            {form.providing_area_id && (
              <div className="border rounded-md p-3 space-y-3 bg-muted/30">
                <Label className="text-xs uppercase text-muted-foreground">Agregar medicación (del stock del área que entrega)</Label>
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={newItem.article_id}
                      onChange={e => setNewItem({ ...newItem, article_id: e.target.value })} data-testid="transfer-item-article">
                      <option value="">Elegí un artículo…</option>
                      {provStock.map(s => <option key={s.article_id} value={s.article_id}>{s.is_medicamento ? '💊 ' : ''}{s.article_name} — disp.: {s.total_stock} {s.unit || unitOf(s.article_id)}</option>)}
                    </select>
                  </div>
                  <div className="w-24">
                    <Input type="number" min="1" placeholder="Cant." value={newItem.quantity}
                      onChange={e => setNewItem({ ...newItem, quantity: e.target.value })} data-testid="transfer-item-qty" />
                  </div>
                  <Button type="button" onClick={addItem} data-testid="transfer-item-add"><Plus className="w-4 h-4" /></Button>
                </div>
                {provStock.length === 0 && <p className="text-xs text-amber-600">El área que entrega no tiene stock disponible.</p>}

                {form.items.length > 0 && (
                  <Table>
                    <TableHeader><TableRow><TableHead>Artículo</TableHead><TableHead className="w-20">Cant.</TableHead><TableHead className="w-10"></TableHead></TableRow></TableHeader>
                    <TableBody>
                      {form.items.map(i => (
                        <TableRow key={i.article_id} data-testid={`transfer-item-row-${i.article_id}`}>
                          <TableCell className="text-sm">{i.article_name}</TableCell>
                          <TableCell className="text-sm">{i.quantity} <span className="text-xs text-muted-foreground">{unitOf(i.article_id)}</span></TableCell>
                          <TableCell><Button size="sm" variant="ghost" className="text-red-600" onClick={() => removeItem(i.article_id)}><Trash2 className="w-4 h-4" /></Button></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            )}

            <div>
              <Label>Observaciones</Label>
              <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Motivo / notas (opcional)" rows={2} data-testid="transfer-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancelar</Button>
            <Button onClick={submitCreate} disabled={submitting} data-testid="transfer-submit">{submitting ? 'Creando…' : 'Crear solicitud'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detalle */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="transfer-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">{detail.transfer_number}
                  <Badge variant="outline" className={STATUS_META[detail.status]?.cls}>{STATUS_META[detail.status]?.label}</Badge>
                </DialogTitle>
                <DialogDescription>{detail.requesting_area_name} ← {detail.providing_area_name}</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div><span className="text-muted-foreground">Solicitó:</span> {detail.created_by_name} · {fmtDate(detail.created_at)}</div>
                  {detail.approved_by_name && <div><span className="text-muted-foreground">Aprobó:</span> {detail.approved_by_name}</div>}
                  {detail.dispatched_by_name && <div><span className="text-muted-foreground">Despachó:</span> {detail.dispatched_by_name}</div>}
                  {detail.received_by_name && <div><span className="text-muted-foreground">Recibió:</span> {detail.received_by_name}</div>}
                </div>
                {detail.notes && <div className="bg-muted/40 rounded p-2"><span className="text-muted-foreground">Obs:</span> {detail.notes}</div>}
                {detail.rejected_reason && <div className="bg-red-50 text-red-700 rounded p-2">Rechazo: {detail.rejected_reason}</div>}
                <div>
                  <p className="font-medium mb-1">Ítems solicitados</p>
                  <Table>
                    <TableHeader><TableRow><TableHead>Artículo</TableHead><TableHead className="w-16">Cant.</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {(detail.items || []).map((i, idx) => (
                        <TableRow key={idx}><TableCell>{i.article_name}</TableCell><TableCell>{i.quantity} <span className="text-xs text-muted-foreground">{unitOf(i.article_id)}</span></TableCell></TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {(detail.dispatch_allocations || []).length > 0 && (
                  <div>
                    <p className="font-medium mb-1">Lotes despachados</p>
                    <Table>
                      <TableHeader><TableRow><TableHead>Artículo</TableHead><TableHead>Lote</TableHead><TableHead className="w-16">Cant.</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {detail.dispatch_allocations.map((a, idx) => (
                          <TableRow key={idx}><TableCell>{a.article_name}</TableCell><TableCell className="text-xs">{a.lot_number || 's/lote'}</TableCell><TableCell>{a.qty}</TableCell></TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
              <DialogFooter>
                {(detail.status === 'dispatched' || detail.status === 'received') && (
                  <Button variant="outline" onClick={() => openPdf(detail.transfer_id)}><FileText className="w-4 h-4 mr-2" />Acuse PDF</Button>
                )}
                <Button variant="outline" onClick={() => setDetail(null)}>Cerrar</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AreaTransfers;
